import { z } from "zod";

const VAT_NUMBER_PATTERN = /^[A-Z]{2}[0-9]{8,12}$/;

const normalizeVatNumber = (value: unknown) => {
  if (typeof value !== "string") return undefined;
  const normalized = value.replace(/\s+/g, "").toUpperCase();
  return normalized.length > 0 ? normalized : undefined;
};

export const RegistrationFormAddressSchema = z.object({
  address1: z.string().min(1),
  address2: z.string().optional(),
  city: z.string().min(1),
  zip: z.string().min(1),
  countryCode: z.string().min(1),
});

export const RegistrationFormUserSchema = z.object({
  title: z.enum(["Mr", "Mrs", "Ms", "Miss", "Dr"]),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.email(),
  phone: z.string().min(1),
});

export const RegistrationFormCompanySchema = z
  .object({
    name: z.string().min(1),
    legalStatus: z.enum(["SOLE_TRADER", "LIMITED_COMPANY", "PARTNERSHIP"]),
    vatNumber: z.preprocess(normalizeVatNumber, z.string().optional()),
    companyRegistrationNumber: z.string().optional(),
    tradeRelationship: z.enum(["INDEPENDENT", "FACIA_GROUP"]),
    faciaGroupId: z.string().optional(),
    faciaId: z.string().optional(),
    user: RegistrationFormUserSchema,
    location: z.object({
      billingSameAsShipping: z.boolean(),
      shippingAddress: RegistrationFormAddressSchema,
      billingAddress: RegistrationFormAddressSchema.optional(),
    }),
  })
  .superRefine((data, ctx) => {
    // INDEPENDENT: cannot have FACIA_GROUP-specific values
    if (data.tradeRelationship === "INDEPENDENT") {
      if (data.faciaGroupId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["faciaGroupId"],
          message: "INDEPENDENT companies cannot select a Facia Group",
        });
      }

      if (data.faciaId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["faciaId"],
          message: "INDEPENDENT companies cannot provide a Facia Group ID",
        });
      }
    }

    // FACIA_GROUP: must have a valid faciaGroupId and a faciaId
    if (data.tradeRelationship === "FACIA_GROUP") {
      if (!data.faciaGroupId?.includes("gid:" + "/" + "/" + "shopify/Metaobject")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["faciaGroupId"],
          message: "Provided faciaGroupId either is malformed or does not exist",
        });
      }

      if (!data.faciaId?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["faciaId"],
          message: "Facia Group ID is required for FACIA_GROUP registrations",
        });
      }
    }

    // LIMITED_COMPANY / PARTNERSHIP: require CRN and VAT number
    if (["LIMITED_COMPANY", "PARTNERSHIP"].includes(data.legalStatus)) {
      if (!data.companyRegistrationNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["companyRegistrationNumber"],
          message:
            "Companies with a LIMITED_COMPANY or PARTNERSHIP legalStatus must provide a company registration number",
        });
      }
      if (!data.vatNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["vatNumber"],
          message:
            "Companies with a LIMITED_COMPANY or PARTNERSHIP legalStatus must provide a VAT number",
        });
      } else if (!VAT_NUMBER_PATTERN.test(data.vatNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["vatNumber"],
          message:
            "VAT number must start with a 2-letter country code followed by 8-12 digits (for example, GB12345678)",
        });
      }
    } else if (data.vatNumber && !VAT_NUMBER_PATTERN.test(data.vatNumber)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["vatNumber"],
        message:
          "VAT number must start with a 2-letter country code followed by 8-12 digits (for example, GB12345678)",
      });
    }
  });

export const RegistrationFormSchema = z.object({
  company: RegistrationFormCompanySchema,
});

export const RegistrationFormCaptchaSchema = z.object({
  token: z.string().min(1),
  action: z.string().min(1),
});

export const RegistrationFormSubmissionSchema = RegistrationFormSchema.extend({
  captcha: RegistrationFormCaptchaSchema,
});

export type RegistrationFormAddress = z.infer<typeof RegistrationFormAddressSchema>;
export type RegistrationFormUser = z.infer<typeof RegistrationFormUserSchema>;
export type RegistrationFormCompany = z.infer<typeof RegistrationFormCompanySchema>;
export type RegistrationForm = z.infer<typeof RegistrationFormSchema>;
export type RegistrationFormCaptcha = z.infer<typeof RegistrationFormCaptchaSchema>;
export type RegistrationFormSubmission = z.infer<typeof RegistrationFormSubmissionSchema>;